import { useCallback, useMemo, useRef, useState } from 'react'

import type { ComboboxOption } from '@/components/ui/combobox'
import type { WarehousePositionLotType } from '@/lib/api/warehouse-position-api'
import { Combobox } from '@/components/ui/combobox'
import { useDebounced } from '@/hooks/use-debounced'
import {
    useWarehousePosition,
    useWarehousePositionsInfinite,
} from '@/hooks/use-warehouse-position-query'
import { m } from '@/lib/paraglide/messages'

interface Props {
    /**
     * Id của control thật, để `<label htmlFor>` trỏ đúng vào trigger. Bộ lọc
     * danh sách bắt buộc có nhãn hiện (R7); form nhập lô không truyền thì vẫn
     * như cũ.
     */
    id?: string
    lotType: WarehousePositionLotType
    value: string | undefined
    onChange: (id: string) => void
    /** Placeholder khi chưa chọn. Mặc định "Vị trí lưu kho" (form nhập lô). */
    placeholder?: string
    /** Cho phép xoá lựa chọn (dùng ở filter danh sách → về "tất cả vị trí"). */
    allowClear?: boolean
    /** Nhãn hiển thị khi `value` nằm ngoài các trang đã tải (vd sửa lô có vị
     *  trí cũ). Thiếu nó thì trigger tụt về placeholder dù đã có giá trị. */
    fallbackLabel?: string
}

function positionLabel(p: { code: string; name?: string | null }): string {
    return p.name ? `${p.code} — ${p.name}` : p.code
}

/** Cỡ trang mỗi lần cuộn: nhỏ để mở ô nhanh, cuộn là có thêm. */
const POSITION_PAGE_SIZE = 30

export function PositionPicker({
    id,
    lotType,
    value,
    onChange,
    placeholder,
    allowClear,
    fallbackLabel,
}: Props) {
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounced(search, 300)

    // Tìm server + cuộn tới đáy tải trang tiếp (rule "Resource-select inputs"
    // ở AGENTS.md): kho có hơn một trang vị trí thì vẫn chọn được hết.
    const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } =
        useWarehousePositionsInfinite({
            lot_type: lotType,
            is_active: true,
            search: debouncedSearch || undefined,
            per_page: POSITION_PAGE_SIZE,
        })

    // Mọi vị trí từng thấy, để vị trí đã chọn giữ nhãn khi đổi từ khoá làm nó
    // rơi khỏi danh sách hiện tại. Ghi trong useMemo: idempotent, cùng pattern
    // `seenRef` của ProductPicker.
    const seenRef = useRef<Map<string, ComboboxOption>>(new Map())
    const options = useMemo(() => {
        const list = (data?.pages ?? [])
            .flatMap((page) => page.data ?? [])
            .map((p) => ({ value: p.id, label: positionLabel(p) }))
        for (const o of list) seenRef.current.set(o.value, o)
        return list
    }, [data])

    // Vị trí đang chọn nằm ngoài các trang đã tải (vd mở danh sách lô đang lọc
    // theo vị trí ở trang 2) mà không có nhãn dự phòng: tra theo id, cùng quyền
    // `warehouse_positions.read` với danh sách. Chờ trang đầu tải xong để không
    // tra thừa vị trí có sẵn trong đó.
    const seen = value ? seenRef.current.get(value) : undefined
    const { data: byId } = useWarehousePosition(
        value && !seen && !fallbackLabel && !isLoading ? value : undefined,
    )
    const fetched = byId?.data
    const selectedOption = value
        ? (seen ??
          (fetched?.id === value
              ? { value, label: positionLabel(fetched) }
              : undefined) ??
          (fallbackLabel ? { value, label: fallbackLabel } : undefined))
        : undefined

    const handleLoadMore = useCallback(() => {
        void fetchNextPage()
    }, [fetchNextPage])

    // Ô tìm kiếm nằm trong popover bị Radix unmount khi đóng; reset state search
    // theo, nếu không mở lại thấy ô rỗng mà danh sách vẫn lọc theo từ khoá cũ.
    const handleOpenChange = useCallback((open: boolean) => {
        if (!open) setSearch('')
    }, [])

    return (
        <Combobox
            id={id}
            options={options}
            value={value ?? ''}
            onChange={onChange}
            onSearchChange={setSearch}
            shouldFilter={false}
            placeholder={placeholder ?? m.lot_position_label()}
            allowClear={allowClear}
            selectedOption={selectedOption}
            onOpenChange={handleOpenChange}
            onLoadMore={handleLoadMore}
            hasMore={Boolean(hasNextPage)}
            loadingMore={isFetchingNextPage}
        />
    )
}
